/**
 * All copy for the onboarding flow. Kept separate from view code so
 * the words — which are the most iterated part of this product — can
 * change without touching any components.
 *
 * The Suron voice rules for everything in this file:
 *   - No em-dashes. They read as AI.
 *   - No corporate cheer. No "Let's get started."
 *   - No exclamation points.
 *   - Short sentences. One thought per line.
 *   - Universal across age, profession, and life stage.
 */

/**
 * The seven intro screens that precede the name input. Each one is a
 * single thought. Tap Next to advance. By the end the user has been
 * walked through authority, surprise, recognition, stakes, and invitation.
 */
export const INTRO_SCREENS: string[] = [
  "Harvard studied **724 lives** for **85 years** to find what actually makes a life good.",
  "Not money. Not career. Not genes.",
  "The quality of their **relationships**.",
  "The people you __tend__ shape who you become.",
  "The ones you let __drift__ shape who you don't.",
  "Most people move **blind**.",
  "You **don't** have to.",
];

/**
 * The name screen headline. Displayed above the text input. The
 * product introduces itself first, then asks for the user's name —
 * a mutual handshake, not a form.
 */
export const NAME_SCREEN = {
  headline: "I'm Meridian.",
  prompt: "What should I call you?",
  placeholder: "Your name",
};

/**
 * The brief that separates the name input from the first voice question.
 * Primes the user to slow down. The word "slowly" is doing most of the
 * work — it tells them this is not a form to rush through.
 */
export const BRIEF_SCREEN = {
  lines: [
    "Four questions.",
    "Answer slowly.",
    "They land different than you expect.",
  ],
};

/**
 * The four voice questions. Each one names a relational pattern:
 *   1. Drift — relationships fade without a fight
 *   2. Frequency is not depth — texting is not knowing
 *   3. The unsaid — someone is always waiting
 *   4. The anchor — one person matters most
 *
 * By the time they finish all four, the user has been taught the
 * four concepts that define Suron's worldview without a single
 * explanation. They have learned by answering.
 */
export const QUESTIONS = [
  {
    id: "drift",
    text: "Who have you slowly stopped talking to for no real reason?",
    teaches: "drift",
  },
  {
    id: "depth",
    text: "Who do you text all the time but don't actually know anymore?",
    teaches: "frequency-is-not-depth",
  },
  {
    id: "unsaid",
    text: "Who's waiting to hear something from you?",
    teaches: "the-unsaid",
  },
  {
    id: "anchor",
    text: "Who's the one person you can't afford to drift from?",
    teaches: "the-anchor",
  },
] as const;

/**
 * The final screen after all four answers are captured. No buttons
 * here — a short pause, then the tab flips to the Me view with the
 * enrolled profile active.
 */
export const COMPLETE_SCREEN = {
  line: "I heard you. Let's begin.",
};
